import { Component, OnInit, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { IntlRelativeTimePipe } from 'angular-ecmascript-intl';
import { Store } from '@ngxs/store';
import { VxBadgeComponent } from '@ui/badge/badge.component';
import { VxButtonComponent } from '@ui/button/button.component';
import { VxCardComponent } from '@ui/card/card.component';
import { VxEmptyStateComponent } from '@ui/empty-state/empty-state.component';
import { VxIconComponent } from '@ui/icon/icon.component';
import { VxItemCellComponent } from '@ui/item-cell/item-cell.component';
import { VxListRowComponent } from '@ui/list-row/list-row.component';
import { VxPanelComponent } from '@ui/panel/panel.component';
import { VxTooltipDirective } from '@ui/tooltip/tooltip.directive';
import { FeatureWrapperComponent } from 'src/app/core/components/feature-wrapper/feature-wrapper.component';
import { SharedModule } from 'src/app/core/shared.module';
import { HasQuota } from 'src/app/helpers/quota';
import { AlertSource } from 'src/app/models/alert';
import { CheckRoleDirective } from 'src/app/services/permission/permission.directive';
import { TranslationService } from 'src/app/services/translation/translation.service';
import {
  ListAlertProcessorsAction,
  LoadAlertSourcesAction,
} from 'src/app/store/alerts/alerts.actions';
import { AlertsState } from 'src/app/store/alerts/alerts.state';
import { SubscriptionState } from 'src/app/store/subscription/subscription.state';
import { alertSourceTypeVM, AlertSourceTypeVM } from '../../alert-source-types';

interface SourceVM {
  source: AlertSource;
  type: AlertSourceTypeVM;
}

@Component({
  selector: 'app-page-settings-alerting-overview',
  templateUrl: 'overview.component.html',
  imports: [
    SharedModule,
    FeatureWrapperComponent,
    VxTooltipDirective,
    CheckRoleDirective,
    IntlRelativeTimePipe,
    VxBadgeComponent,
    VxButtonComponent,
    VxCardComponent,
    VxEmptyStateComponent,
    VxIconComponent,
    VxItemCellComponent,
    VxListRowComponent,
    VxPanelComponent,
  ],
})
export class AlertingOverviewSettingsPageComponent implements OnInit {
  private store = inject(Store);
  private router = inject(Router);

  /** Shared by the label rows and list rows so the columns line up. */
  protected readonly columnTemplate =
    'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)';

  protected readonly processorColumnTemplate =
    'minmax(0, 1.3fr) minmax(0, 0.7fr) minmax(0, 1fr) minmax(0, 1fr)';

  protected currentLocale = inject(TranslationService).currentLocale$;

  protected readonly columnLabelClasses =
    'truncate text-xs font-medium tracking-wide text-zinc-500 uppercase';

  private alertSourceEntities = this.store.selectSignal(
    AlertsState.alertSources
  );
  private alertProcessorEntities = this.store.selectSignal(
    AlertsState.alertProcessors
  );
  private processorsFeature = this.store.selectSignal(
    SubscriptionState.feature('alerting-processors')
  );
  private sourcesFeature = this.store.selectSignal(
    SubscriptionState.feature('alerting-sources')
  );

  protected alertProcessors = computed(() =>
    Object.values(this.alertProcessorEntities().entities)
      .map(entity => entity.data)
      .filter(processor => !!processor)
  );

  protected alertSources = computed<SourceVM[]>(() =>
    Object.values(this.alertSourceEntities().entities)
      .map(entity => entity.data)
      .filter((source): source is AlertSource => !!source)
      .map(source => ({
        source,
        type: alertSourceTypeVM(source.type),
      }))
  );

  protected hasAlertProcessorsQuota = computed(() => {
    const feature = this.processorsFeature();
    return feature ? HasQuota(feature) : false;
  });

  protected hasAlertSourcesQuota = computed(() => {
    const feature = this.sourcesFeature();
    return feature ? HasQuota(feature) : false;
  });

  ngOnInit(): void {
    this.store.dispatch(new LoadAlertSourcesAction());
    this.store.dispatch(new ListAlertProcessorsAction());
  }

  protected goToCreateAlertSource() {
    this.router.navigate(['/settings/alerting/sources/new']);
  }

  protected goToCreateAlertProcessor() {
    this.router.navigate(['/settings/alerting/processors/new']);
  }
}
